import { isNetworkError } from "./network";

type UnknownRecord = Record<string, unknown>;

export type ErrorReportFields = {
  name?: string;
  message: string;
  stack?: string;
  details: string;
  fingerprint: string;
};

const DEFAULT_REPORT_MESSAGE = "Unknown error";
const REDACTED_VALUE = "[redacted]";
const MAX_FINGERPRINT_LENGTH = 500;

const SENSITIVE_KEY_VALUE_PATTERN =
  /\b(sessionToken|accessToken|refreshToken|idToken|clientSecret|authorization|password|api[_-]?key|secret)\b\s*[:=]\s*["']?[^"',\s}]+/gi;
const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+\b/gi;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function stringifyUnknown(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: stringifyUnknown((value as Error & { cause?: unknown }).cause, seen),
    };
  }

  if (typeof value === "bigint") {
    return `${value}n`;
  }

  if (typeof value === "function") {
    return `[function ${value.name || "anonymous"}]`;
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  if (seen.has(value)) {
    return "[circular]";
  }

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((entry) => stringifyUnknown(entry, seen));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => [
      key,
      stringifyUnknown(entryValue, seen),
    ]),
  );
}

function extractMessageCandidate(error: unknown): string | undefined {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (!isRecord(error)) {
    return undefined;
  }

  const directMessage = error.message;
  if (typeof directMessage === "string" && directMessage.trim()) {
    return directMessage;
  }

  const nestedCandidates = [
    error.error,
    error.data,
    error.cause,
    isRecord(error.data) ? error.data.error : undefined,
  ];

  for (const candidate of nestedCandidates) {
    const nestedMessage = extractMessageCandidate(candidate);
    if (nestedMessage?.trim()) {
      return nestedMessage;
    }
  }

  return undefined;
}

export function redactSensitiveErrorText(value: string): string {
  return value
    .replace(SENSITIVE_KEY_VALUE_PATTERN, "$1=" + REDACTED_VALUE)
    .replace(BEARER_TOKEN_PATTERN, `Bearer ${REDACTED_VALUE}`);
}

export function clampErrorReportText(value: string, limit: number): string {
  if (value.length <= limit) {
    return value;
  }

  return `${value.slice(0, limit)}\n[truncated]`;
}

export function createErrorFingerprint(
  name: string | undefined,
  message: string,
  stack: string | undefined,
): string {
  const stackHead = stack?.split("\n").slice(0, 3).join("|") ?? "";
  return [name || "Error", message, stackHead]
    .join(":")
    .slice(0, MAX_FINGERPRINT_LENGTH);
}

export function buildErrorReportFields(
  error: unknown,
  fallbackMessage = DEFAULT_REPORT_MESSAGE,
): ErrorReportFields {
  const name =
    error instanceof Error
      ? error.name || undefined
      : isRecord(error) && typeof error.name === "string"
        ? error.name
        : undefined;

  const stack =
    error instanceof Error
      ? error.stack
      : isRecord(error) && typeof error.stack === "string"
        ? error.stack
        : undefined;

  const message = redactSensitiveErrorText(
    extractMessageCandidate(error)?.trim() || fallbackMessage,
  );

  let details: string;
  if (typeof error === "string") {
    details = error;
  } else if (error instanceof Error) {
    details = error.stack || `${error.name}: ${error.message}`;
  } else {
    try {
      const serialized = JSON.stringify(stringifyUnknown(error), null, 2);
      details = serialized || String(error ?? fallbackMessage);
    } catch {
      details = String(error ?? fallbackMessage);
    }
  }

  const redactedDetails = redactSensitiveErrorText(details);
  const redactedStack = stack ? redactSensitiveErrorText(stack) : undefined;

  return {
    name,
    message,
    stack: redactedStack,
    details: redactedDetails,
    fingerprint: createErrorFingerprint(name, message, redactedStack),
  };
}

export function shouldReportRuntimeError(
  error: unknown,
  fallbackMessage = DEFAULT_REPORT_MESSAGE,
): boolean {
  const fields = buildErrorReportFields(error, fallbackMessage);
  const combined = [fields.message, fields.stack, fields.details]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join("\n");

  return !isNetworkError(combined);
}
